import { Field, Form, Formik } from "formik";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { CustomFieldWithError } from "../../../../../components/forms/CustomFieldWithError";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { useMutation } from "react-query";
import { updateUserDetails } from "../../../../../services/onboarding.service";
import { useTranslation } from "react-i18next";
import { MyAreaTitle } from "../components";
import { UserAvatar } from "./UserAvatar";
import DatePicker from "react-datepicker";
import * as yup from "yup";
import de from "date-fns/locale/de";
import enUS from "date-fns/locale/en-US";
import { getLocaleFromLanguage } from "../../../../../services/content.service";
import { useContext, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "libphonenumber-js";
export function PersonalInformation() {
  const { user, setUser, configurations } = useContext(AuthContext);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { t } = useTranslation("myArea");
  const { t: onboardingT } = useTranslation("onboarding");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userNameErrorMessage, setUserNameErrorMessage] = useState<
    string | null
  >(null);

  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const updateUserDetailsMutation = useMutation(updateUserDetails, {
    onSuccess: (response) => {
      setUser(response);
      setErrorMessage("");
      setUserNameErrorMessage("");
    },
    onError: (error: any) => {
      console.log("Error: ", error);
      if (error?.response?.data?.message === "MOBILE_PHONE_DUPLICATED") {
        setErrorMessage(`${onboardingT("stepName.duplicated")}`);
      } else if (error?.response?.data?.message === "USERNAME_DUPLICATED") {
        setUserNameErrorMessage(
          `${t("profile.username-duplicated") || "Username already exists"}`
        );
      }
    },
  });

  const shouldRenderBirthday = configurations?.includesBirthday;
  const shouldRenderAvatar = configurations?.includesCommunity;

  const monthsLocal = getLocaleFromLanguage();
  const localeMapping = {
    de: de,
    "en-US": enUS,
  };

  const validateGermanPhoneLength = (
    phoneNumber: string,
    countryCode: string
  ): boolean => {
    if (countryCode !== "DE") return true;

    const germanDialCode = "49";

    const digitsOnly = phoneNumber.replace(/\D/g, "");

    let nationalNumber = digitsOnly;
    if (digitsOnly.startsWith(germanDialCode)) {
      nationalNumber = digitsOnly.substring(germanDialCode.length);
    }

    if (nationalNumber.startsWith("0")) {
      nationalNumber = nationalNumber.substring(1);
    }

    if (nationalNumber.charAt(0) !== "1") {
      return false;
    }

    return nationalNumber.length === 10 || nationalNumber.length === 11;
  };

  const convertedLocale = localeMapping[monthsLocal];

  return (
    <div className="mb-5">
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("profile.title")} />
      </div>

      <div className="flex flex-col lg:flex-row w-full">
        {shouldRenderAvatar && (
          <div className="w-full lg:w-1/4 rounded-full flex h-fit justify-center pt-5">
            <UserAvatar />
          </div>
        )}
        <div className="w-full lg:w-3/4 lg:px-6">
          <Formik
            initialValues={{
              username: user?.username || "",
              firstName: user?.userDetails?.firstName || "",
              lastName: user?.userDetails?.lastName || "",
              email: user?.email || "",
              mobilePhone: user?.userDetails?.mobilePhone || "",
              city: user?.userDetails?.city || "",
              birthday: user?.userDetails?.birthday
                ? new Date(user.userDetails.birthday)
                    .toISOString()
                    .substr(0, 10)
                : "",
            }}
            validationSchema={yup.object({
              username: yup
                .string()
                .required("Username is required")
                .min(3, `${t("profile.min-characters")}`)
                .matches(/^[\w.-]+$/, `${t("no-space-username-warning")}`),
              // ... other validation schemas ...
            })}
            onSubmit={(values) => {
              const userDetails = {
                firstName: values.firstName || null,
                lastName: values.lastName || null,
                username: values.username || null,
                mobilePhone: values.mobilePhone || null,
                city: values.city || null,
                birthday: values.birthday || null,
              };

              updateUserDetailsMutation.mutate(
                {
                  userDetails,
                },
                {
                  onSuccess: (response) => {
                    setUser(response);
                    setIsFormDirty(false); // Reset the form's dirty state
                  },
                  onError: (error) => {
                    console.log("Error: ", error);
                  },
                }
              );
            }}
          >
            {({ values, setFieldValue, errors, touched, isValid }) => {
              return (
                <Form
                  className="mt-4 md:grid md:grid-cols-2 md:gap-3 lg:grid-cols-none"
                  onChange={() => setIsFormDirty(true)}
                >
                  {shouldRenderAvatar && (
                    <div className="w-full lg:w-full">
                      <label htmlFor="username">{t("profile.nickname")}</label>
                      <div>
                        <CustomFieldWithError
                          className="w-full h-10 mt-1 border-black rounded-lg"
                          type="username"
                          id="username"
                          name="username"
                          error={errors.username || null}
                          touched={!!touched.username}
                        />
                      </div>
                      {userNameErrorMessage && (
                        <p className="text-red-500 text-sm mt-1">
                          {userNameErrorMessage}
                        </p>
                      )}
                    </div>
                  )}
                  <div
                    className={`w-full mt-4 md:mt-0 ${
                      shouldRenderAvatar ? "lg:mt-4" : ""
                    }  lg:w-full`}
                  >
                    <label htmlFor="firstName">{t("profile.first-name")}</label>
                    <div>
                      <CustomFieldWithError
                        className="w-full h-10 mt-1 border-black rounded-lg"
                        type="firstName"
                        id="firstName"
                        name="firstName"
                        error={errors.firstName || null}
                        touched={!!touched.firstName}
                      />
                    </div>
                  </div>
                  <div className="w-full mt-4 md:mt-0 lg:mt-4 lg:w-full">
                    <label htmlFor="lastName">{t("profile.last-name")}</label>
                    <div>
                      <CustomFieldWithError
                        className="w-full h-10 mt-1 border-black rounded-lg"
                        type="lastName"
                        id="lastName"
                        name="lastName"
                        error={errors.lastName || null}
                        touched={!!touched.lastName}
                      />
                    </div>
                  </div>

                  {shouldRenderBirthday && (
                    <div className="w-full mt-4 md:mt-0 lg:mt-4 lg:w-full">
                      <label htmlFor="date-picker">
                        {t("profile.birthday")}
                      </label>
                      <div>
                        <DatePicker
                          id="date-picker"
                          selected={
                            values.birthday ? new Date(values.birthday) : null
                          }
                          onChange={(date: any) => {
                            setFieldValue(
                              "birthday",
                              date?.toISOString().substr(0, 10)
                            );
                            setIsFormDirty(true);
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={
                            t("select-a-date") || "Select a date"
                          }
                          className="w-full h-10 mt-1 border-black rounded-lg"
                          locale={convertedLocale}
                        />
                      </div>
                    </div>
                  )}

                  <div className="w-full mt-4 md:mt-0 lg:mt-4 lg:w-full">
                    <label htmlFor="city">{onboardingT("stepName.city")}</label>
                    <div>
                      <CustomFieldWithError
                        className="w-full h-10 mt-1 border-black rounded-lg"
                        type="city"
                        id="city"
                        name="city"
                        error={errors.city || null}
                        touched={!!touched.city}
                        placeholder={`${onboardingT(
                          "stepName.cityPlaceHolder"
                        )}`}
                      />
                    </div>
                  </div>

                  <div className="w-full mt-4 md:mt-0 lg:mt-4 lg:w-full">
                    <label htmlFor="mobilePhone">
                      {onboardingT("stepName.mobilePhone")}
                    </label>

                    <div className="border border-black rounded-lg w-full mt-1">
                      <PhoneInput
                        country={"de"}
                        value={values.mobilePhone}
                        onChange={(value, country, event, formattedValue) => {
                          if (!value) {
                            setFieldValue("mobilePhone", "");
                            setIsPhoneValid(true);
                            setIsFormDirty(true);
                            return;
                          }

                          setFieldValue("mobilePhone", value);
                          setIsFormDirty(true);

                          try {
                            const dialCode = (country as any)?.dialCode || "";
                            let nationalPart = "";

                            if (value.startsWith(dialCode)) {
                              nationalPart = value.substring(dialCode.length);
                            }

                            if (nationalPart.startsWith("0")) {
                              const correctedValue =
                                dialCode + nationalPart.substring(1);

                              console.log("Prevented leading zero:", {
                                originalValue: value,
                                correctedValue,
                              });

                              setFieldValue("mobilePhone", correctedValue);

                              value = correctedValue;
                            } else {
                              setFieldValue("mobilePhone", value);
                            }

                            setIsFormDirty(true);

                            const countryCode =
                              (country as any)?.countryCode?.toUpperCase() ||
                              "";
                            const fullNumber = value.startsWith("+")
                              ? value
                              : `+${value}`;

                            const isValid = isValidPhoneNumber(
                              fullNumber,
                              countryCode
                            );
                            const lengthValidation = validatePhoneNumberLength(
                              fullNumber,
                              countryCode
                            );
                            const hasValidLength =
                              lengthValidation !== "TOO_SHORT" &&
                              lengthValidation !== "TOO_LONG" &&
                              lengthValidation !== "INVALID_LENGTH" &&
                              lengthValidation !== "NOT_A_NUMBER" &&
                              lengthValidation !== "INVALID_COUNTRY";

                            const isPossible = isPossiblePhoneNumber(
                              fullNumber,
                              countryCode
                            );

                            const hasValidGermanLength =
                              validateGermanPhoneLength(
                                fullNumber,
                                countryCode
                              );
                            const isGermanNumberValid =
                              countryCode !== "DE" || hasValidGermanLength;

                            setIsPhoneValid(
                              isValid &&
                                hasValidLength &&
                                isPossible &&
                                isGermanNumberValid
                            );
                          } catch (error) {
                            console.log("Phone validation error:", error);
                            setIsPhoneValid(false);
                          }
                        }}
                        countryCodeEditable={true}
                        enableSearch={true}
                        disableSearchIcon={false}
                        searchPlaceholder={
                          onboardingT("stepName.search") || "Search"
                        }
                        placeholder={
                          onboardingT("stepName.mobilePhonePlaceHolder") ||
                          "+49 123 456 7890"
                        }
                        containerStyle={{
                          width: "100%",
                          height: "100%",
                        }}
                        inputStyle={{
                          width: "100%",
                          height: "auto",
                          padding: "0.4rem 0.75rem 0.4rem 52px",
                          fontSize: "0.875rem",
                          lineHeight: "1.5",
                          borderRadius: "0.5rem",
                          background: "transparent",
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                        buttonStyle={{
                          border: "none",
                          borderRadius: "0",
                          background: "transparent",
                          padding: "2px",
                        }}
                        dropdownStyle={{
                          border: "1px solid black",
                          borderRadius: "0.5rem",
                          marginTop: "5px",
                        }}
                      />
                      <style>
                        {`
              /* 🔹 Remove the search icon completely */
            .react-tel-input .search-emoji {
  display: none !important;
  visibility: hidden !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

              /* 🔹 Apply Figtree font to all input fields */
              .react-tel-input input,
              .react-tel-input .search-box,
              .react-tel-input .country-list {
                font-family: 'Figtree', source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace !important;
                font-size: 1rem !important;
              }

      /* 🔹 Style the country list with horizontal padding */
.react-tel-input .country-list {
  padding-left: 5px !important; 
  padding-right: 5px !important; 
  display: flex;
  flex-direction: column; /* Keep the list vertical */
}

              /* 🔹 Style the search box */
              .react-tel-input .search-box {
                border-radius: 0.5rem !important; 
                padding: 0.5rem !important;
                background-color: white !important;
            }
                .react-tel-input input:focus {
  border: 1px solid #007bff !important; /* Change color to your desired focus color (e.g., blue) */
}
  `}
                      </style>
                    </div>
                    {errorMessage && (
                      <p className="text-red-500 text-sm mt-1">
                        {errorMessage}
                      </p>
                    )}
                    {!isPhoneValid && (
                      <p className="text-red-500 text-sm mt-1">
                        {onboardingT("stepName.invalidPhone") ||
                          "Invalid phone number"}
                      </p>
                    )}
                  </div>

                  <div className="w-full mt-4 md:mt-0 lg:mt-4 lg:w-full">
                    <label htmlFor="email">{t("profile.email")}</label>
                    <div>
                      <CustomFieldWithError
                        disabled={true}
                        className="w-full h-10 mt-1 border-black rounded-lg"
                        type="email"
                        id="email"
                        name="email"
                        error={errors.email || null}
                        touched={!!touched.email}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className={`mt-6 w-full ${
                      isFormDirty ? "" : "bg-gray-200"
                    }`}
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.MEDIUM}
                    disabled={!isFormDirty || !isPhoneValid || !isValid}
                  >
                    {t("profile.save")}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
}
